import { forwardRef, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { clsx } from 'clsx';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { HeadingNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';

import { FormError } from '@components/common/Forms/FormError/FormError';
import { mergeRefs } from '@utils/refs/merge-refs';

import { LexicalEditor } from 'lexical';
import { LexicalEditorType } from '@type/lexical';
import ImagesPlugin from './plugins/ImagesPlugin';
import HtmlPlugin from './plugins/HtmlPlugin';
import { ImageNode } from './nodes/ImageNode';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import defaultTheme from './themes/defaultTheme';


/** Lexical editor component props */
interface LexicalProps {
  className?: string;
  defaultValue?: string;
  handleHtmlDescription?: (html: string) => void;
  handleTextDescription?: (text: string) => void;
  required?: boolean;
  autoFocus?: boolean;
  isValid?: boolean;
  dataTestId?: string;
}

/** Lexical editor styles */
const lexicalStyles = {
  base: 'block relative w-full border rounded bg-surface shadow-sm text-ink transition-all duration-200',
  focus: 'focus-within:border-primary-light/80 focus-within:ring focus-within:ring-primary-light focus-within:ring-opacity-40',
  error: 'border-error focus-within:border-error/80 focus-within:ring focus-within:ring-error focus-within:ring-opacity-40',
  valid: 'border-secondary',
  required: 'border-warning',
  optional: 'border-input',
};

/**
 * @description Lexical editor component.
 * - `className` - additional classes for the editor.
 * - `defaultValue` - the initial value of the editor (html string, ie '<p>Some initial value</p>').
 * - `handleHtmlDescription` - the callback function to get the modified html content (string with html tags).
 * - `handleTextDescription` - the callback function to get the modified text content (string without html tags).
 * - `required` - if the editor is required.
 * - `autoFocus` - if the editor should be focused on load; default true.
 * - `isValid` - the validity of the editor; use this to show the error message in Lexical.
 *
 * **For checking the editor's validity on form submission, use the {@link useLexicalValidity} hook.**
 *
 * @example
 * // This is needed in order to get the editor instance
 * const editorRef = useRef<LexicalEditorType>(null);
 *
 * // Use the validity hook (ignore if not needed)
 * const {
 *  checkLexicalValidity, htmlContent, setHtmlContent, setTextContent, isValid,
 * } = useLexicalValidity(editorRef?.current);
 *
 * // BjForm hook (ignore if not needed)
 * const {
 *  handleCallbackSubmit, record, checkFormValidation,
 * } = useBjForm({ wizardData: { description: htmlContent } });
 *
 * // If you make the editor required, you need to check its validity before submitting the form
 * // That means you can't just call handleCallbackSubmit(e, submitCallback) directly
 * const submitForm = (e: FormEvent<HTMLFormElement>) => {
 *  // This is a bit redundant (handleCallbackSubmit also checks form elements validity), but it's a way to verify both
 *  // the formData and the editor validity at the same time.
 *  e.preventDefault();
 *  checkFormValidation(e);
 *  checkLexicalValidity();
 *  // Go normally if the editor and form elements are valid
 *  return handleCallbackSubmit(e, submitCallback);
 * };
 *
 * // Inside render, use this component
 * <Lexical
 *  required
 *  handleHtmlDescription={setHtmlContent}
 *  handleTextDescription={setTextContent}
 *  autoFocus={false}
 *  ref={editorRef}
 *  isValid={isValid}
 * />
 */
export const Lexical = forwardRef<LexicalEditor | null, LexicalProps>((props, ref) => {
  // Destructure props
  const {
    className,
    defaultValue,
    handleHtmlDescription,
    handleTextDescription,
    required,
    autoFocus,
    isValid,
    dataTestId,
  } = props;

  // Editor configuration
  // *********************************
  const editorConfig = {
    namespace: 'LexicalEditor',
    onError: (error: Error) => {
      throw error;
    },
    theme: defaultTheme,
    // Any custom nodes go here
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      ImageNode,
    ],
    disableEvents: true,
  };

  // Translation
  const { t } = useTranslation('common');
  // Internal editor ref
  const editorRef = useRef<LexicalEditorType>(null);
  // Check if the editor has content (for styling)
  const [hasContent, setHasContent] = useState<boolean>(!!defaultValue);

  // Get the text content of the editor (no html tags)
  // *********************************
  const onTextContentChange = (text: string) => {
    if (handleTextDescription) handleTextDescription(text);
    setHasContent(!!text);
  };

  // Render the editor
  // *********************************
  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div className={clsx({
        [lexicalStyles.base]: true,
        [lexicalStyles.optional]: !required && !hasContent,
        [lexicalStyles.required]: required && !hasContent && isValid === undefined,
        [lexicalStyles.error]: required && isValid === false,
        [lexicalStyles.valid]: hasContent,
        [lexicalStyles.focus]: hasContent || isValid === undefined,
        className,
      })}
      >
        {/* the header of the editor with the buttons */}
        <ToolbarPlugin />
        <div
          className="min-h-12"
          {... dataTestId && { 'data-test-id': dataTestId }}
        >
          {/* default plugin for text edit */}
          <RichTextPlugin
            placeholder={null}
            contentEditable={<ContentEditable className="min-h-96 scroll-mt-20 p-3 outline-0" />}
            ErrorBoundary={LexicalErrorBoundary}
          />

          {/* custom plugin for image upload */}
          <ImagesPlugin />

          {/* default history plugin for undo and redo buttons */}
          <HistoryPlugin />

          {/* default plugin for ordered and unordered list */}
          <ListPlugin />

          {/* custom plugin to load the initial html content and get the modified content for saving with the form */}
          <HtmlPlugin
            onHtmlChanged={handleHtmlDescription}
            onTextChanged={onTextContentChange}
            {...defaultValue && { initialHtml: defaultValue }}
            autoFocus={autoFocus}
          />

          {/* get the editor instance outside (for useLexicalValidity). */}
          <EditorRefPlugin editorRef={mergeRefs([ref, editorRef])} />
        </div>
      </div>

      {/* Form error message */}
      {required && (
        <FormError
          errorMessage={t('form.validity.value-missing')}
          isShowing={isValid === false}
        />
      )}
    </LexicalComposer>
  );
});

